import { getLocale } from '@tov-plus/ui';
import { buildGuestSiteUrl, guestAppConfig } from '@tov-plus/config';
import { toGuestJourneyPath } from '@tov-plus/domain';
import {
  formatEnumLabel,
  formatCountLabel,
  TovButton,
  Input,
  TovField,
  msg,
  TovCard,
  useToast,
} from '@tov-plus/ui';
import { useTovQueryClient } from '@tov-plus/frontend-data';
import { invalidateQueryGroups } from '@tov-plus/frontend-data';
import { useEffect, useState } from 'react';
import { NavLink, useSearchParams } from '@tov-plus/frontend-data';

import { guestApi } from '../app/api';
import { useGuestAccess } from '../contexts/GuestAccessContext';
import { guestQueryKeys } from '../lib/data/guestQueryKeys';
import { formatGuestDateTime } from '../lib/localization/guestLocale';
import {
  parseRecentGuestAccessEntries,
  recentGuestAccessStorageKey,
  type RecentGuestAccessEntry,
} from '../lib/storage/recentGuestAccess';

function formatToken(value: string) {
  return formatEnumLabel(value);
}

export function AccessPage() {
  const access = useGuestAccess();
  const queryClient = useTovQueryClient();
  const notifications = useToast();
  const [searchParams] = useSearchParams();
  const reason = searchParams.get('reason');
  const [recoveryRequest, setRecoveryRequest] = useState('');
  const [recentInvites, setRecentInvites] = useState<RecentGuestAccessEntry[]>(
    [],
  );
  const [secureResumePacket, setSecureResumePacket] = useState<{
    resumeCode: string;
    expiresAt: string;
    resumePath: string | null;
  } | null>(null);
  const [secureResumeStatus, setSecureResumeStatus] = useState<
    'idle' | 'issuing'
  >('idle');

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      setRecentInvites(
        parseRecentGuestAccessEntries(
          window.localStorage.getItem(recentGuestAccessStorageKey),
        ),
      );
    } catch {
      setRecentInvites([]);
    }
  }, []);

  if (access.status !== 'ready') {
    return null;
  }

  const resolvedLocaleCode = getLocale();
  const occasionTimezone = access.experience.occasion.timezone;

  const invitedEventCount = access.experience.journeySummary.invitedEventCount;
  const participantCount =
    access.experience.identitySummary.participantDisplayNames.length;
  const recommendedPath = toGuestJourneyPath(
    access.experience.journeySummary.recommendedTarget,
  );
  const recoveryContext = [
    msg('pages.access_page.continuity_note_occasion').replace('{value}', () =>
      String(access.experience.occasion.displayName),
    ),
    msg('pages.access_page.continuity_note_slug').replace(
      '{value}',
      () => access.occasionSlug ?? '',
    ),
    msg('pages.access_page.continuity_note_guest').replace(
      '{value}',
      () => access.experience.guestDisplayName,
    ),
    msg('pages.access_page.continuity_note_issue').replace(
      '{value}',
      () =>
        recoveryRequest || msg('pages.access_page.recovery_request_default'),
    ),
  ].join('\n');
  const continuityPacket = [
    msg('pages.access_page.continuity_note_slug').replace(
      '{value}',
      () => access.occasionSlug ?? '',
    ),
    msg('pages.access_page.continuity_note_guest_name').replace(
      '{value}',
      () => access.experience.guestDisplayName,
    ),
    msg('pages.access_page.continuity_note_recommended_route').replace(
      '{value}',
      () => recommendedPath,
    ),
    msg('pages.access_page.continuity_note_secure_step'),
  ].join('\n');
  const readyOccasionSlug = access.occasionSlug ?? '';
  const readyGuestToken = access.guestToken ?? '';
  const readOnly = access.isReadOnly;
  const readyResumePath = 'resumePath' in access ? access.resumePath : null;

  async function copyAccessText(contents: string) {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(contents);
      } catch {
        notifications.error(
          msg('pages.access_page.pages_access_page_copy_unavailable'),
        );
        return;
      }
      notifications.success(
        msg('pages.access_page.pages_access_page_access_details_copied'),
      );
      return;
    }

    notifications.error(
      msg('pages.access_page.pages_access_page_copy_unavailable'),
    );
  }

  async function issueSecureResumePacket() {
    setSecureResumeStatus('issuing');

    try {
      const response = await guestApi.createSecureResumeCode(
        readyOccasionSlug,
        readyGuestToken,
        {
          resume_path: readyResumePath ?? recommendedPath,
        },
      );
      setSecureResumePacket({
        resumeCode: response.resumeCode,
        expiresAt: response.expiresAt,
        resumePath: response.resumePath,
      });
      await invalidateQueryGroups(queryClient, [
        guestQueryKeys.access,
        guestQueryKeys.bootstrap(readyOccasionSlug, readyGuestToken),
      ]);
      const resumeUrl = buildGuestSiteUrl({
        occasionSlug: readyOccasionSlug,
        path: '/access',
        browserOrigin:
          typeof window === 'undefined' ? undefined : window.location.origin,
        query: {
          resume_code: response.resumeCode,
        },
      });

      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(resumeUrl);
        notifications.success(
          msg('pages.access_page.pages_access_page_secure_resume_link_copied'),
        );
      } else {
        notifications.warning(
          msg('pages.access_page.pages_access_page_secure_resume_code_ready'),
          response.resumeCode,
        );
      }
    } catch {
      notifications.error(
        msg('pages.access_page.pages_access_page_secure_resume_failed'),
      );
    } finally {
      setSecureResumeStatus('idle');
    }
  }

  return (
    <section className="page-grid">
      <TovCard variant="content" as="div" isWide>
        <p className="page-eyebrow">{msg('app.navigation.access')}</p>
        {reason ===
        msg('pages.access_page.pages_access_page_session_expired') ? (
          <p className="state-panel__meta">
            {msg(
              'pages.access_page.pages_access_page_your_guest_session_has_expired_please_open_your',
            )}{' '}
          </p>
        ) : null}
        <h2>{access.experience.guestDisplayName}</h2>
        <div className="hero-card__stats">
          <article>
            <strong>{invitedEventCount}</strong>
            <span>{msg('app.guest_workflows.invited_events')}</span>
          </article>
          <article>
            <strong>{participantCount}</strong>
            <span>
              {msg('pages.access_page.pages_access_page_people_covered')}
            </span>
          </article>
          <article>
            <strong>
              {access.experience.identitySummary.responseUnitDisplayName ??
                msg('pages.access_page.pages_access_page_individual_invite')}
            </strong>
            <span>
              {msg('pages.access_page.pages_access_page_invite_scope')}
            </span>
          </article>
        </div>
        <p>{access.experience.identitySummary.summary}</p>
        <div className="detail-list">
          <article>
            <strong>{msg('pages.home_page.pages_home_page_start_here')}</strong>
            <span>
              {access.experience.journeySummary.summary}{' '}
              {msg(
                'pages.access_page.pages_access_page_open_the_recommended_route_first_so_the_guest',
              )}{' '}
            </span>
          </article>
        </div>
        <div className="action-row">
          <TovButton asChild>
            <NavLink to={recommendedPath}>
              {access.experience.journeySummary.recommendedLabel}
            </NavLink>
          </TovButton>
          <TovButton
            onClick={() => {
              const continuitySummary = [
                msg('pages.access_page.continuity_note_occasion').replace(
                  '{value}',
                  () => String(access.experience.occasion.displayName),
                ),
                msg('pages.access_page.continuity_note_invite').replace(
                  '{value}',
                  () => String(access.experience.guestDisplayName),
                ),
                msg('pages.access_page.continuity_note_people').replace(
                  '{value}',
                  () => String(participantCount),
                ),
                msg('pages.access_page.continuity_note_route').replace(
                  '{value}',
                  () => String(access.resumePath ?? recommendedPath),
                ),
              ].join('\n');

              void copyAccessText(continuitySummary);
            }}
            type="button"
            variant="secondary"
          >
            {msg(
              'pages.access_page.pages_access_page_copy_continuity_note',
            )}{' '}
          </TovButton>
          {!readOnly ? (
            <TovButton
              onClick={() => void issueSecureResumePacket()}
              type="button"
              variant="secondary"
            >
              {secureResumeStatus === 'issuing'
                ? msg(
                    'pages.access_page.pages_access_page_issuing_secure_resume',
                  )
                : msg(
                    'pages.access_page.pages_access_page_copy_secure_resume_packet',
                  )}
            </TovButton>
          ) : null}
          <TovButton asChild variant="secondary">
            <NavLink to="/events">
              {msg('pages.access_page.pages_access_page_review_invite_details')}
            </NavLink>
          </TovButton>
          <TovButton onClick={() => access.clearStoredToken()} type="button">
            {msg('app.router.app_router_clear_saved_invite')}{' '}
          </TovButton>
          <TovButton asChild variant="secondary">
            <a href={`mailto:${guestAppConfig.supportEmail}`}>
              {msg('app.router.app_router_contact_support')}{' '}
            </a>
          </TovButton>
        </div>
      </TovCard>

      <TovCard variant="content" as="div">
        <p className="page-eyebrow">
          {msg('pages.access_page.pages_access_page_current_access_mode')}
        </p>
        <div className="detail-list">
          <article>
            <strong>
              {msg('pages.access_page.pages_access_page_occasion_slug')}
            </strong>
            <span>{access.occasionSlug}</span>
          </article>
          <article>
            <strong>
              {msg('pages.access_page.pages_access_page_token_present')}
            </strong>
            <span>
              {access.guestToken
                ? msg('general.booleans.yes')
                : msg('general.booleans.no')}
            </span>
          </article>
          <article>
            <strong>{msg('components.color_picker.mode')}</strong>
            <span>{formatEnumLabel(access.experience.accessMode)}</span>
          </article>
          <article>
            <strong>{msg('general.fields.locale.label')}</strong>
            <span>
              {access.experience.localizationContext.resolvedLocaleCode}{' '}
              {msg('pages.access_page.pages_access_page_via')}{' '}
              {formatEnumLabel(
                access.experience.localizationContext.localeSource,
              )}
            </span>
          </article>
          <article>
            <strong>
              {msg('pages.access_page.pages_access_page_journey_phase')}
            </strong>
            <span>
              {formatEnumLabel(access.experience.journeySummary.phase)}
            </span>
          </article>
          <article>
            <strong>{msg('general.fields.lifecycle.label')}</strong>
            <span>
              {formatToken(access.experience.accessLifecycle.status)}
              {access.experience.accessLifecycle.expiresAt
                ? msg('pages.access_page.valid_until').replace(
                    '{expires_at}',
                    () => access.experience.accessLifecycle.expiresAt!,
                  )
                : ''}
            </span>
          </article>
          <article>
            <strong>
              {msg('pages.access_page.pages_access_page_invite_covers')}
            </strong>
            <span>
              {formatCountLabel(participantCount, {
                one: msg('pages.access_page.participant_one'),
                other: msg('pages.access_page.participant_other'),
              })}{' '}
              {msg('pages.access_page.pages_access_page_across')}{' '}
              {formatCountLabel(invitedEventCount, {
                one: msg('pages.access_page.invited_event_one'),
                other: msg('pages.access_page.invited_event_other'),
              })}
            </span>
          </article>
        </div>
      </TovCard>

      <TovCard variant="content" as="div">
        <p className="page-eyebrow">
          {msg('pages.access_page.pages_access_page_identity_and_grouping')}
        </p>
        <div className="detail-list">
          <article>
            <strong>
              {msg('pages.access_page.pages_access_page_response_unit')}
            </strong>
            <span>
              {access.experience.identitySummary.responseUnitDisplayName ??
                msg('pages.access_page.pages_access_page_individual_guest')}
            </span>
          </article>
          <article>
            <strong>{msg('general.fields.scope.label')}</strong>
            <span>
              {formatToken(access.experience.identitySummary.responseScope)}
            </span>
          </article>
          <article>
            <strong>
              {msg('pages.access_page.pages_access_page_participants')}
            </strong>
            <span>
              {access.experience.identitySummary.participantDisplayNames.join(
                ', ',
              ) ||
                msg(
                  'pages.access_page.pages_access_page_no_linked_participants',
                )}
            </span>
          </article>
          <article>
            <strong>
              {msg('pages.access_page.pages_access_page_delegates')}
            </strong>
            <span>
              {access.experience.identitySummary.delegateDisplayNames.join(
                ', ',
              ) || msg('pages.access_page.pages_access_page_no_delegates')}
            </span>
          </article>
          <article>
            <strong>
              {msg('pages.access_page.pages_access_page_households')}
            </strong>
            <span>
              {access.experience.identitySummary.householdLabels.join(', ') ||
                msg(
                  'pages.access_page.pages_access_page_no_household_grouping',
                )}
            </span>
          </article>
        </div>
        <p>{access.experience.identitySummary.summary}</p>
      </TovCard>

      <TovCard variant="content" as="div">
        <p className="page-eyebrow">
          {msg('pages.access_page.pages_access_page_continuity')}
        </p>
        <div className="detail-list">
          <article>
            <strong>
              {msg(
                'pages.access_page.pages_access_page_returning_on_this_device',
              )}
            </strong>
            <span>
              {msg(
                'pages.access_page.pages_access_page_your_invite_token_can_be_restored_locally_so',
              )}{' '}
            </span>
          </article>
          <article>
            <strong>
              {msg(
                'pages.access_page.pages_access_page_invalid_invite_recovery',
              )}
            </strong>
            <span>
              {msg(
                'pages.access_page.pages_access_page_if_the_invite_changes_or_expires_clear_the',
              )}{' '}
            </span>
          </article>
          <article>
            <strong>
              {msg('pages.access_page.pages_access_page_need_help')}
            </strong>
            <span>
              {msg(
                'pages.access_page.pages_access_page_support_can_help_if_the_occasion_slug_or',
              )}{' '}
            </span>
          </article>
          <article>
            <strong>
              {msg('pages.access_page.pages_access_page_entry_source')}
            </strong>
            <span>{access.entryIntent.source.replaceAll('_', ' ')}</span>
          </article>
          <article>
            <strong>
              {msg('pages.access_page.pages_access_page_resume_target')}
            </strong>
            <span>
              {access.resumePath ??
                msg(
                  'pages.access_page.pages_access_page_no_saved_return_path_yet',
                )}
            </span>
          </article>
          <article>
            <strong>
              {msg(
                'pages.access_page.pages_access_page_recommended_next_route',
              )}
            </strong>
            <span>{recommendedPath}</span>
          </article>
          <article>
            <strong>
              {msg('pages.access_page.pages_access_page_safe_recovery')}
            </strong>
            <span>{access.experience.accessLifecycle.summary}</span>
          </article>
          <article>
            <strong>
              {msg(
                'pages.access_page.pages_access_page_account_optional_continuity',
              )}
            </strong>
            <span>
              {msg(
                'pages.access_page.pages_access_page_the_same_invite_can_resume_on_another_device',
              )}{' '}
            </span>
          </article>
          <article>
            <strong>
              {msg('pages.access_page.pages_access_page_cross_device_handoff')}
            </strong>
            <span>
              {msg(
                'pages.access_page.pages_access_page_use_the_secure_continuity_packet_below_to_move',
              )}{' '}
            </span>
          </article>
          {secureResumePacket ? (
            <article>
              <strong>
                {msg(
                  'pages.access_page.pages_access_page_secure_resume_packet_active',
                )}
              </strong>
              <span>
                {msg('pages.access_page.pages_access_page_expires')}{' '}
                {formatGuestDateTime(
                  secureResumePacket.expiresAt,
                  resolvedLocaleCode,
                  occasionTimezone,
                )}
                {secureResumePacket.resumePath
                  ? ` • ${msg('pages.access_page.route')} ${secureResumePacket.resumePath}`
                  : ''}
              </span>
            </article>
          ) : null}
        </div>
        {recentInvites.length > 0 ? (
          <div className="detail-list detail-list--stacked">
            {recentInvites.map((item) => (
              <article key={`recent:${item.occasionSlug}`}>
                <strong>{item.occasionDisplayName}</strong>
                <span>
                  {item.guestDisplayName}{' '}
                  {msg('pages.access_page.pages_access_page_last_opened')}{' '}
                  {formatGuestDateTime(
                    item.seenAt,
                    resolvedLocaleCode,
                    item.timezone ?? occasionTimezone,
                  )}
                </span>
                <span>
                  {item.resumePath ??
                    msg(
                      'pages.access_page.pages_access_page_overview_route_saved_for_this_invite',
                    )}
                </span>
              </article>
            ))}
          </div>
        ) : null}
        <div className="action-row">
          <TovButton asChild variant="secondary">
            <NavLink to="/messages">
              {msg('pages.home_page.pages_home_page_open_message_center')}
            </NavLink>
          </TovButton>
          <TovButton asChild variant="secondary">
            <NavLink to="/recent-invites">
              {msg('app.navigation.recent_invites')}
            </NavLink>
          </TovButton>
          <TovButton asChild variant="secondary">
            <NavLink to="/help">
              {msg('pages.access_page.pages_access_page_guest_help')}
            </NavLink>
          </TovButton>
          <TovButton
            type="button"
            variant="secondary"
            onClick={() => {
              void copyAccessText(continuityPacket);
            }}
          >
            {msg(
              'pages.access_page.pages_access_page_copy_secure_continuity_packet',
            )}{' '}
          </TovButton>
        </div>
        <TovField label={msg('app.router.app_router_request_latest_invite')}>
          <Input
            placeholder={msg(
              'pages.access_page.pages_access_page_add_a_short_note_if_the_old_link',
            )}
            value={recoveryRequest}
            onChange={(event) => setRecoveryRequest(event.target.value)}
          />
        </TovField>
        <div className="action-row">
          <TovButton asChild variant="secondary">
            <a
              href={`mailto:${guestAppConfig.supportEmail}?subject=${encodeURIComponent(msg('pages.access_page.recovery_request_subject'))}&body=${encodeURIComponent(recoveryContext)}`}
            >
              {msg('app.router.app_router_request_latest_invite')}{' '}
            </a>
          </TovButton>
          <TovButton
            type="button"
            variant="secondary"
            onClick={() => {
              void copyAccessText(recoveryContext);
            }}
          >
            {msg(
              'pages.access_page.pages_access_page_copy_recovery_request',
            )}{' '}
          </TovButton>
        </div>
        <div className="detail-list">
          <article>
            <strong>
              {msg('pages.access_page.pages_access_page_recovery_checklist')}
            </strong>
            <span>
              {msg(
                'pages.access_page.pages_access_page_1_reopen_the_newest_host_provided_invite',
              )}{' '}
            </span>
          </article>
          <article>
            <strong>
              {msg(
                'pages.access_page.pages_access_page_when_forwarded_links_fail',
              )}
            </strong>
            <span>
              {msg(
                'pages.access_page.pages_access_page_forwarded_or_partially_copied_invite_links_often_miss',
              )}{' '}
            </span>
          </article>
        </div>
      </TovCard>
    </section>
  );
}
